import type { CSSProperties } from 'react'
import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'
import { AppSidebar } from '@/components/app/AppSidebar'
import { GenerateWorkspaceProvider } from '@/context/GenerateWorkspaceContext'
import { useStorageStore } from '@/stores/storage'
import { usePresetsStore } from '@/stores/presets'
import { useKeysStore } from '@/stores/keys'

const ROUTE_LABELS: Record<string, string> = {
  '/apps/google/generate-images': 'Generate Images',
  '/apps/google/generate-videos': 'Generate Videos',
  '/apps/google/generate-actors': 'AI Actors',
  '/apps/google/generate-image-to-video': 'Image to Video',
  '/apps/studio/character': 'Character',
  '/apps/cloudflare-ai/generate-images': 'Generate Images',
  '/apps/replicate/generate-images': 'Generate Images',
  '/apps/blueprint': 'Blueprint',
  '/apps/blueprints/global': 'Global Blueprints',
  '/apps/blueprints/mine': 'My Blueprints',
  '/apps/presets': 'Presets',
  '/apps/settings': 'Settings',
}

function PageBreadcrumb() {
  const { pathname } = useLocation()
  const label =
    Object.entries(ROUTE_LABELS).find(
      ([route]) => pathname === route || pathname.startsWith(route + '/'),
    )?.[1] ?? ''

  if (!label) return null
  return <span className="text-sm font-medium text-foreground">{label}</span>
}

export function AppLayout() {
  const { pathname } = useLocation()
  const keysInit = useKeysStore((s) => s.init)
  const storageInit = useStorageStore((s) => s.init)
  const presetsInit = usePresetsStore((s) => s.init)

  useEffect(() => {
    const doInit = async () => {
      await keysInit() // storageInit calls getKey — must run first
      await storageInit()
      void presetsInit() // independent of keys
    }
    void doInit()
  }, [keysInit, storageInit, presetsInit])
  const sidebarWidth = '22rem'

  return (
    <GenerateWorkspaceProvider key={pathname}>
      <SidebarProvider style={{ '--sidebar-width': sidebarWidth } as CSSProperties}>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="h-4" />
            <PageBreadcrumb />
          </header>
          <div className="flex flex-1 flex-col overflow-auto">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </GenerateWorkspaceProvider>
  )
}
